import React, { useEffect, useMemo, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import moment from "moment";
import { message } from "antd";
import Header from "../components/Header";
import Loader from "../components/Loader";
import Sidebar from "../components/Sidebar";
import SubHeader from "../components/SubHeader";
import { pageRoutes } from "../routes/PageRoutes";
import { curSym } from "../auth/Pip";
import {
  getBoatData,
  getActiveStaffData,
  getSupplierData
} from "../redux/actions/staffActions";
import {
  fetchPresets,
  generateMaintenanceTask
} from "../redux/actions/servicePresetsActions";
import { CreateTask } from "../redux/actions/maintainedBoatsActions";

const CreateMaintenance = () => {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const { isLoading1, all_boats, staff_active_data, supplier_data } = useSelector(
    (state) => state?.staffReducer
  );
  const { isLoading, serviceList } = useSelector(
    (state) => state?.servicePresetsReducer
  );
  const { isLoading2 } = useSelector(
    (state) => state?.maintainedReducer
  );

  const [isToggle, setIsToggle] = useState(false);

  // Form states
  const [boatId, setBoatId] = useState("");
  const [title, setTitle] = useState("");
  const [type, setType] = useState("General");
  const [priority, setPriority] = useState("Medium");
  const [status, setStatus] = useState("Planned");
  const [description, setDescription] = useState("");
  const [scheduledDate, setScheduledDate] = useState("");
  const [scheduledTime, setScheduledTime] = useState("");
  const [estimatedDuration, setEstimatedDuration] = useState("");
  const [errors, setErrors] = useState({});

  const [assignTo, setAssignTo] = useState(""); // None, Technician, Supplier
  const [assigneeId, setAssigneeId] = useState("");

  // Services list
  const [selectedServiceId, setSelectedServiceId] = useState("");
  const [addedServices, setAddedServices] = useState([]);

  // Custom service fields on-the-fly
  const [customServiceName, setCustomServiceName] = useState("");
  const [customServiceCost, setCustomServiceCost] = useState("");

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    dispatch(getBoatData());
    dispatch(fetchPresets());
    dispatch(getActiveStaffData());
    dispatch(getSupplierData());
  }, [dispatch]);

  const onHandleClick = () => {
    setIsToggle(!isToggle);
  };

  // Reset Assignee when AssignTo changes
  useEffect(() => {
    setAssigneeId("");
  }, [assignTo]);

  // Selected Boat Details
  const selectedBoat = useMemo(() => {
    return (all_boats || []).find((b) => String(b.id) === String(boatId));
  }, [all_boats, boatId]);

  // Handle Preset Service Adding
  const handleAddPresetService = (serviceId) => {
    if (!serviceId) return;
    const preset = (serviceList || []).find((s) => String(s.id) === String(serviceId));
    if (preset && !addedServices.some((s) => s.id === preset.id)) {
      setAddedServices([
        ...addedServices,
        {
          id: preset.id,
          serviceTitle: preset.serviceTitle,
          serviceCost: preset.serviceCost,
          isCustom: false,
        },
      ]);
    }
    setSelectedServiceId("");
  };

  // Handle Custom Service Adding
  const handleAddCustomService = () => {
    if (!customServiceName.trim() || !customServiceCost) {
      message.error("Please fill in both custom service name and cost.");
      return;
    }
    const cost = parseFloat(customServiceCost);
    if (isNaN(cost) || cost < 0) {
      message.error("Please enter a valid positive cost.");
      return;
    }
    const customServiceObj = {
      id: "custom-" + Date.now(),
      serviceTitle: customServiceName.trim(),
      serviceCost: cost,
      isCustom: true,
    };
    setAddedServices([...addedServices, customServiceObj]);
    setCustomServiceName("");
    setCustomServiceCost("");
    message.success("Custom service added to scope!");
  };

  const handleRemoveService = (id) => {
    setAddedServices(addedServices.filter((s) => s.id !== id));
  };

  const totalAmount = useMemo(() => {
    return addedServices.reduce((sum, item) => sum + parseFloat(item.serviceCost || 0), 0);
  }, [addedServices]);

  const availableServices = useMemo(() => {
    return (serviceList || []).filter(
      (item) => !addedServices.some((added) => !added.isCustom && added.id === item.id)
    );
  }, [serviceList, addedServices]);

  // Validate form entries
  const validateForm = (isDirectJob = true) => {
    if (!boatId) {
      message.error("Please select a boat.");
      return false;
    }
    if (!title.trim()) {
      message.error("Please enter a maintenance title.");
      return false;
    }
    if (addedServices.length === 0) {
      message.error("Please add at least one service to the scope of work.");
      return false;
    }

    if (!assignTo) {
      message.error("Please select an Assign To option.");
      return false;
    }

    let tempErrors = {};
    let isValid = true;

    if (scheduledDate) {
      const today = moment().startOf("day");
      let selected = moment(scheduledDate, ["YYYY-MM-DD", "DD-MM-YYYY", "MM-DD-YYYY", "YYYY/MM/DD", "DD/MM/YYYY"]).startOf("day");
      if (!selected.isValid()) {
        selected = moment(new Date(scheduledDate)).startOf("day");
      }

      if (!selected.isValid()) {
        tempErrors.scheduledDate = "Please select a valid scheduled date.";
        isValid = false;
      } else if (selected.isBefore(today)) {
        tempErrors.scheduledDate = "Please select today or a future date.";
        isValid = false;
      } else if (selected.isSame(today) && scheduledTime) {
        const currentTime = moment();
        let selectedTimeAndDate = moment(scheduledDate + "T" + scheduledTime);
        if (!selectedTimeAndDate.isValid()) {
          const timeParts = scheduledTime.split(":");
          selectedTimeAndDate = moment().startOf("day").hours(Number(timeParts[0])).minutes(Number(timeParts[1]));
        }
        if (selectedTimeAndDate.isBefore(currentTime)) {
          tempErrors.scheduledTime = "Please select today or a future time.";
          isValid = false;
        }
      }
    }

    if (isDirectJob) {
      if (!scheduledDate) {
        tempErrors.scheduledDate = "Please select a scheduled date.";
        isValid = false;
      }
      if (assignTo === "None") {
        message.error("Please assign this job to a technician or supplier.");
        isValid = false;
      }
      if (!assigneeId) {
        message.error("Please select an assignee.");
        isValid = false;
      }
    }

    setErrors(tempErrors);
    return isValid;
  };

  // Submit action 1: Create direct maintenance task/job
  const handleCreateMaintenance = async () => {
    if (!validateForm(true)) return;

    setIsSubmitting(true);

    const normalPresetIds = addedServices.filter((s) => !s.isCustom).map((s) => Number(s.id));
    const customServices = addedServices.filter((s) => s.isCustom).map((s) => ({
      serviceTitle: s.serviceTitle,
      serviceCost: s.serviceCost,
    }));

    const payload = {
      boatId: Number(boatId),
      description: title.trim(),
      taskInfo: description.trim(),
      time_alloted: estimatedDuration ? String(estimatedDuration) : "0",
      quoted_value: totalAmount.toString(),
      isRecurring: 0,
      assigned_to: assignTo === "Technician" ? "STAFF" : "OUTSOURCED",
      date_scheduled_from: scheduledDate,
      date_scheduled_to: scheduledDate,
      services: normalPresetIds,
      customServices: customServices,
    };

    if (assignTo === "Technician") {
      payload.assignStaffId = Number(assigneeId);
      payload.scheduled_start_time = scheduledDate + "T" + (scheduledTime || "00:00") + ":00";
    } else {
      payload.supplierIds = [Number(assigneeId)];
      if (scheduledTime) {
        payload.scheduled_start_time = scheduledDate + "T" + scheduledTime + ":00";
      }
    }

    const callback = (response) => {
      setIsSubmitting(false);
      if (response?.success) {
        message.success("Maintenance task created and assigned successfully!");
        navigate(pageRoutes.maintenance);
      } else {
        message.error(response?.message || "Failed to create maintenance task");
      }
    };

    dispatch(CreateTask({ payload, callback }));
  };

  // Submit action 2: Generate Quote manually & send approval to owner
  const handleGenerateQuote = async () => {
    if (!validateForm(false)) return;

    setIsSubmitting(true);

    const normalPresetIds = addedServices.filter((s) => !s.isCustom).map((s) => Number(s.id));
    const customServices = addedServices.filter((s) => s.isCustom).map((s) => ({
      serviceTitle: s.serviceTitle,
      serviceCost: s.serviceCost,
    }));

    const payload = {
      boatId: Number(boatId),
      description: title.trim(),
      taskInfo: description.trim(),
      services: normalPresetIds,
      customServices: customServices,
    };

    const callback = (response) => {
      setIsSubmitting(false);
      if (response?.success) {
        message.success("Quote generated and sent to boat owner successfully!");
        navigate(pageRoutes.maintenance);
      } else {
        message.error(response?.message || "Failed to generate quote");
      }
    };

    dispatch(generateMaintenanceTask({ payload, callback }));
  };

  if (isLoading || isLoading1 || isLoading2) {
    return <Loader />;
  }

  return (
    <div className="ct_dashbaord_bg">
      <div className={`ct_dashbaord_main ${isToggle === false && "ct_active"}`}>
        <Sidebar path="maintenance-details" />
        <div className="ct_content_right">
          <Header onClick={onHandleClick} />
          <div className="ct_px_23 ct_mt_30 pb-5">
            <div className="d-flex justify-content-between ct_flex_col_575 gap-3">
              <SubHeader />
            </div>

            <div className="ct_px_12 ct_mt_30 pb-5">
              <div className="container-fluid">
                <div className="row mt-4">
                  {/* Left Column: Form Details */}
                  <div className="col-xl-8">
                    {/* SECTION 1: Maintenance Details */}
                    <div className="ct_white_bg_1 mb-4">
                      <h3 className="ct_fs_18 ct_fw_700 ct_text_dark mb-3">1. Maintenance Details</h3>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Maintenance Title *</label>
                          <input
                            type="text"
                            placeholder="Enter Title"
                            className="form-control ct_input ct_transparent_input"
                            value={title}
                            onChange={(e) => setTitle(e.target.value)}
                          />
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">Maintenance Type *</label>
                          <select
                            className="form-control ct_input ct_transparent_input ct_pointer_curser"
                            value={type}
                            onChange={(e) => setType(e.target.value)}
                          >
                            <option value="General">General</option>
                            <option value="Repair">Repair</option>
                            <option value="Inspection">Inspection</option>
                            <option value="Cleaning">Cleaning</option>
                          </select>
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">Priority *</label>
                          <select
                            className="form-control ct_input ct_transparent_input ct_pointer_curser"
                            value={priority}
                            onChange={(e) => setPriority(e.target.value)}
                          >
                            <option value="Low">Low</option>
                            <option value="Medium">Medium</option>
                            <option value="High">High</option>
                            <option value="Critical">Critical</option>
                          </select>
                        </div>

                        <div className="col-md-6 mb-3">
                          <label className="form-label">Status *</label>
                          <select
                            className="form-control ct_input ct_transparent_input ct_pointer_curser"
                            value={status}
                            onChange={(e) => setStatus(e.target.value)}
                          >
                            <option value="Planned">Planned</option>
                            <option value="In Progress">In Progress</option>
                            <option value="Completed">Completed</option>
                          </select>
                        </div>

                        <div className="col-md-12 mb-3">
                          <label className="form-label">Description / Notes</label>
                          <textarea
                            rows={3}
                            placeholder="Enter Description"
                            className="form-control ct_input ct_transparent_input h-auto"
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                          />
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">Scheduled Date *</label>
                          <input
                            type="date"
                            className="form-control ct_input ct_transparent_input"
                            value={scheduledDate}
                            onChange={(e) => {
                              setScheduledDate(e.target.value);
                              if (errors.scheduledDate) {
                                setErrors((prev) => ({ ...prev, scheduledDate: "" }));
                              }
                            }}
                            min={moment().format("YYYY-MM-DD")}
                            style={{ borderColor: errors.scheduledDate ? "red" : "" }}
                          />
                          {errors.scheduledDate && (
                            <span className="d-block mt-1" style={{ color: "red", fontSize: "12px" }}>
                              {errors.scheduledDate}
                            </span>
                          )}
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">Scheduled Time</label>
                          <input
                            type="time"
                            className="form-control ct_input ct_transparent_input"
                            value={scheduledTime}
                            onChange={(e) => {
                              setScheduledTime(e.target.value);
                              if (errors.scheduledTime) {
                                setErrors((prev) => ({ ...prev, scheduledTime: "" }));
                              }
                            }}
                            style={{ borderColor: errors.scheduledTime ? "red" : "" }}
                          />
                          {errors.scheduledTime && (
                            <span className="d-block mt-1" style={{ color: "red", fontSize: "12px" }}>
                              {errors.scheduledTime}
                            </span>
                          )}
                        </div>

                        <div className="col-md-4 mb-3">
                          <label className="form-label">Est. Duration (hrs)</label>
                          <input
                            type="number"
                            placeholder="e.g. 2"
                            className="form-control ct_input ct_transparent_input"
                            value={estimatedDuration}
                            onChange={(e) => setEstimatedDuration(e.target.value)}
                          />
                        </div>
                      </div>
                    </div>

                    {/* SECTION 2: Assign To */}
                    <div className="ct_white_bg_1 mb-4">
                      <h3 className="ct_fs_18 ct_fw_700 ct_text_dark mb-3">2. Assign To</h3>
                      <div className="row">
                        <div className="col-md-6 mb-3">
                          <label className="form-label">Assign To *</label>
                          <select
                            className="form-control ct_input ct_transparent_input ct_pointer_curser"
                            value={assignTo}
                            onChange={(e) => setAssignTo(e.target.value)}
                          >
                            <option value="">Select an option</option>
                            <option value="None">None / Pending Owner Quote Approval</option>
                            <option value="Technician">Technician</option>
                            <option value="Supplier">Supplier</option>
                          </select>
                        </div>

                        {assignTo !== "None" && (
                          <div className="col-md-6 mb-3">
                            <label className="form-label">Select Assignee *</label>
                            {assignTo === "Technician" ? (
                              <select
                                className="form-control ct_input ct_transparent_input ct_pointer_curser"
                                value={assigneeId}
                                onChange={(e) => setAssigneeId(e.target.value)}
                              >
                                <option value="">Select Technician</option>
                                {staff_active_data?.map((item) => (
                                  <option key={item.id} value={item.id}>
                                    {item.full_name || item.email}
                                  </option>
                                ))}
                              </select>
                            ) : (
                              <select
                                className="form-control ct_input ct_transparent_input ct_pointer_curser"
                                value={assigneeId}
                                onChange={(e) => setAssigneeId(e.target.value)}
                              >
                                <option value="">Select Assignee</option>
                                {supplier_data?.map((item) => (
                                  <option key={item.id} value={item.id}>
                                    {item.company_name || item.email}
                                  </option>
                                ))}
                              </select>
                            )}
                          </div>
                        )}
                      </div>
                    </div>

                    {/* SECTION 3: Scope of Work */}
                    <div className="ct_white_bg_1 mb-4">
                      <h3 className="ct_fs_18 ct_fw_700 ct_text_dark mb-3">3. Scope of Work</h3>

                      {/* Preset Services Dropdown */}
                      <div className="form-group mb-4">
                        <label className="form-label">Select Preset Services</label>
                        <select
                          className="form-control ct_input ct_transparent_input ct_pointer_curser"
                          value={selectedServiceId}
                          onChange={(e) => handleAddPresetService(e.target.value)}
                          disabled={!availableServices.length}
                        >
                          <option value="">
                            {availableServices.length ? "Select Preset Service" : "All preset services added"}
                          </option>
                          {availableServices.map((item) => (
                            <option key={item.id} value={item.id}>
                              {item?.serviceTitle} ({curSym}
                              {Number(item?.serviceCost || 0).toFixed(2)})
                            </option>
                          ))}
                        </select>
                      </div>

                      {/* Custom Services Section */}
                      <div className="ct_light_orange_bg p-3 ct_border_radius_12 mb-4">
                        <h4 className="ct_fs_15 ct_fw_700 ct_text_theme mb-2">Add Custom Service on the Fly</h4>
                        <div className="row align-items-end">
                          <div className="col-md-6 mb-2">
                            <label className="form-label ct_fs_12">Service Name</label>
                            <input
                              type="text"
                              placeholder="e.g. Special Polish"
                              className="form-control ct_input ct_transparent_input bg-white"
                              value={customServiceName}
                              onChange={(e) => setCustomServiceName(e.target.value)}
                            />
                          </div>
                          <div className="col-md-4 mb-2">
                            <label className="form-label ct_fs_12">Service Cost ({curSym})</label>
                            <input
                              type="number"
                              placeholder="e.g. 150"
                              className="form-control ct_input ct_transparent_input bg-white"
                              value={customServiceCost}
                              onChange={(e) => setCustomServiceCost(e.target.value)}
                            />
                          </div>
                          <div className="col-md-2 mb-2 text-end">
                            <button
                              type="button"
                              className="ct_orange_btn w-100 py-2 ct_fs_12 text-nowrap"
                              style={{ height: "44px", display: "flex", alignItems: "center", justifyContent: "center" }}
                              onClick={handleAddCustomService}
                            >
                              Add
                            </button>
                          </div>
                        </div>
                      </div>

                      {/* Added Scope list */}
                      <h4 className="ct_fs_16 ct_fw_700 ct_text_dark mb-3">Added Services / Scope</h4>
                      <div className="maintennce-service-wrapper d-grid gap-3">
                        {addedServices.length ? (
                          addedServices.map((item) => (
                            <div className="service-row" key={item.id}>
                              <div className="service-left">
                                <div className="service-title ct_fs_16 ct_fw_700 ct_text_dark">
                                  {item.serviceTitle}
                                  {item.isCustom && <span className="badge bg-info ms-2 ct_fs_10 text-white">Custom</span>}
                                </div>
                              </div>
                              <div className="service-right">
                                <div className="price-block text-center">
                                  <div className="price-label ct_fs_12 ct_fw_700 ct_text_para mb-1">PRICE</div>
                                  <div className="price-value ct_fs_14 ct_fw_700">
                                    <span className="dollar ct_text_para">
                                      {curSym}
                                    </span>
                                    {Number(item.serviceCost || 0).toFixed(2)}
                                  </div>
                                </div>
                                <button
                                  type="button"
                                  className="remove-btn"
                                  onClick={() => handleRemoveService(item.id)}
                                  aria-label={`Remove ${item.serviceTitle}`}
                                >
                                  <svg width="22" height="22" viewBox="0 0 22 22" fill="none" xmlns="http://www.w3.org/2000/svg">
                                    <path d="M5.4 18L4 16.6L9.6 11L4 5.4L5.4 4L11 9.6L16.6 4L18 5.4L12.4 11L18 16.6L16.6 18L11 12.4L5.4 18Z" fill="#808C9D" />
                                  </svg>
                                </button>
                              </div>
                            </div>
                          ))
                        ) : (
                          <div className="text-center py-4 ct_text_para border rounded bg-light">
                            No services added yet. Select preset or add custom services.
                          </div>
                        )}
                      </div>
                    </div>
                  </div>

                  {/* Right Column: Sidebars and Actions */}
                  <div className="col-xl-4">
                    <div className="position-sticky top-0">
                      {/* Boat Information */}
                      <div className="ct_white_bg_1 mb-3">
                        <h4 className="ct_fs_16 ct_fw_700 ct_text_dark mb-3">Boat Selection</h4>
                        <div className="form-group mb-3">
                          <select
                            className="form-control ct_input ct_transparent_input ct_pointer_curser"
                            value={boatId}
                            onChange={(e) => setBoatId(e.target.value)}
                          >
                            <option value="">Select Boat</option>
                            {all_boats?.map((item) => (
                              <option key={item.id} value={item.id}>
                                {item?.rego ? `${item.rego} – ` : ""}{item?.name}
                              </option>
                            ))}
                          </select>
                        </div>
                        {selectedBoat ? (
                          <div className="pt-2 border-top">
                            <h5 className="ct_fs_14 ct_fw_700 ct_text_dark mb-2">Boat Details</h5>
                            <p className="mb-1 ct_fs_13 ct_grid_2_col "><span className="ct_text_para">Name</span><span>:</span> <strong>{selectedBoat.name}</strong></p>
                            <p className="mb-1 ct_fs_13 ct_grid_2_col"><span className="ct_text_para">Rego</span><span>:</span> <strong>{selectedBoat.rego || "-"}</strong></p>
                            <p className="mb-1 ct_fs_13 ct_grid_2_col"><span className="ct_text_para">HIN/VIN</span><span>:</span> <strong>{selectedBoat.vin || "-"}</strong></p>
                            <p className="mb-1 ct_fs_13 ct_grid_2_col"><span className="ct_text_para">Owner</span><span>:</span> <strong>{selectedBoat.owners_name || "-"}</strong></p>
                            <p className="mb-0 ct_fs_13 ct_grid_2_col"><span className="ct_text_para">Email</span><span>:</span> <strong>{selectedBoat.email || "-"}</strong></p>
                          </div>
                        ) : (
                          <div className="text-center py-2 ct_text_para ct_fs_13 border rounded bg-light">
                            Select a boat to view details.
                          </div>
                        )}
                      </div>

                      {/* Quote Preview */}
                      <div className="ct_white_bg_1 mb-3">
                        <h3 className="ct_fs_16 ct_fw_700 mb-3 ct_text_dark">Quote Preview</h3>
                        <div className="maintenance_bill_list">
                          {addedServices.length ? (
                            addedServices.map((item) => (
                              <div className="d-flex justify-content-between gap-3 align-items-start mb-2" key={item.id}>
                                <div className="text-start">
                                  <h4 className="ct_fs_14 ct_fw_600 mb-0">{item.serviceTitle}</h4>
                                </div>
                                <div>
                                  <p className="ct_fs_14 ct_fw_700 ct_text_dark mb-0">
                                    {curSym}
                                    {Number(item.serviceCost || 0).toFixed(2)}
                                  </p>
                                </div>
                              </div>
                            ))
                          ) : (
                            <p className="ct_fs_13 ct_text_para mb-3">Add services to preview the quote.</p>
                          )}

                          <hr className="my-2" />
                          <div className="d-flex justify-content-between gap-3 align-items-start">
                            <h5 className="ct_fs_15 ct_fw_700 mb-1 ct_text_dark">Total Amount</h5>
                            <div className="text-end">
                              <p className="ct_fs_20 ct_fw_800 mb-0 ct_text_theme">
                                {curSym}
                                {totalAmount.toFixed(2)}
                              </p>
                              <p className="ct_fs_11 ct_fw_400 ct_text_para mb-0">Estimated (Excl. Tax)</p>
                            </div>
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="d-grid gap-2">
                        {assignTo === "None" ? (
                          <button
                            type="button"
                            className="ct_orange_btn py-3 w-100"
                            onClick={handleGenerateQuote}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Processing..." : "Generate Quote & Send to Owner"}
                          </button>
                        ) : (
                          <button
                            type="button"
                            className="ct_orange_btn py-3 w-100"
                            onClick={handleCreateMaintenance}
                            disabled={isSubmitting}
                          >
                            {isSubmitting ? "Processing..." : "Create & Allocate Job"}
                          </button>
                        )}
                        <button
                          type="button"
                          className="btn btn-outline-secondary py-3 w-100"
                          onClick={() => navigate(pageRoutes.maintenance)}
                          disabled={isSubmitting}
                        >
                          Cancel
                        </button>
                      </div>
                    </div>
                  </div>

                </div>
              </div>
            </div>

          </div>
        </div>
      </div>
    </div>
  );
};

export default CreateMaintenance;

